import { ref, computed } from "vue";
import { cloneDeep, mergeWith, merge, keyBy } from "lodash-es";
import formInitial from "@/components/builder/form-initials";
import defaultStyling from "@/components/builder/default-styling";
import defaultPageStyling from "@/components/builder/default-page-styling";
import { useFormStore } from "@/stores/form";
import { getFormSurveyDetails } from "@/api/forms";

export function useFormLoader() {
  const formStore = useFormStore();
  const loading = ref(true);

  // Computed property
  const formElementsMap = computed(() => keyBy(formInitial, "type"));

  // Methods
  function formElementCustomizer(initial, resp, key) {
    if ((key === "options" || key === "columns" || key === "rows") && resp) {
      return resp;
    }
  }

  function assignDefaultValue(element) {
    element.defaultValue = element.value;

    if (element.children) {
      element.children.forEach((child) => {
        assignDefaultValue(child);
      });
    }
  }

  function processNestedComponents(components, elementsCollection) {
    components.forEach((nestedEl) => {
      assignDefaultValue(nestedEl);
      elementsCollection.push(nestedEl);
    });
  }

  function processChildren(children) {
    children.forEach((child) => {
      assignDefaultValue(child);
    });
  }

  function processFormBuilder(formBuilderData) {
    formStore.clearFormElements();

    const processedForm = [];
    const elementsToAdd = [];

    for (let el of Object.values(formBuilderData)) {
      const processedElement = mergeWith(
        cloneDeep(formElementsMap.value[el.type]),
        el,
        formElementCustomizer,
      );
      processedForm.push(processedElement);

      // Do not add page breaks to form elements
      if (processedElement.type !== "page-break") {
        assignDefaultValue(processedElement);

        // Collect element for batch update
        elementsToAdd.push(processedElement);

        // Process nested and child components
        if (processedElement.children) {
          processChildren(processedElement.children);
        }
        if (processedElement.components) {
          processNestedComponents(processedElement.components, elementsToAdd);
        }
      }
    }

    formStore.updateFormElements(elementsToAdd);

    return processedForm;
  }

  // Merge page styling with defaults
  function mergePageStyling(pagesData) {
    return pagesData.map((page) => ({
      ...page,
      pageStyling: merge(cloneDeep(defaultPageStyling), page.pageStyling),
    }));
  }

  // Process complete form data from JSON response and store in formStore
  function loadFromJson(jsonData) {
    const processedData = { ...jsonData };

    // Process form elements
    processedData.formBuilder = processFormBuilder(processedData.formBuilder);

    // Merge styling with defaults
    processedData.styling = merge(
      cloneDeep(defaultStyling),
      processedData.styling,
    );

    // Process pages with default styling
    const rawPages = Object.values(processedData.formPages) || [];
    processedData.formPages = mergePageStyling(rawPages);

    formStore.setFormData(processedData);
    return processedData;
  }

  // Load form data from API and process it
  async function loadFromApi(uuid, params = {}) {
    loading.value = true;
    try {
      const { data } = await getFormSurveyDetails(uuid, params);
      const processedData = loadFromJson(data);
      return processedData;
    } catch (error) {
      console.log("Not able to get data");
    } finally {
      loading.value = false;
    }
  }

  return {
    loading,
    loadFromJson,
    loadFromApi,
  };
}
